import React, { Component } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import { Modal, Button, IconLocation } from '../../../components';

import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import css from './SearchFiltersMobile.module.css';

const OPENER_ID = 'SearchFiltersMobile.openFilters';
const HEADING_ID = 'SearchFiltersMobile.heading';

// The filters panel is a modal dialog (D39, A10): rendered in the portal, so the page is inert while it is
// open (Modal keeps Tab in the panel, Esc closes it); focus goes to the heading on open, back to «Фильтры» on close.
class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false };

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  // Open the filters panel; focus moves to its heading (not to a button that Enter would trigger)
  openFilters() {
    this.props.onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true }, () => {
      document.getElementById(HEADING_ID)?.focus();
    });
  }

  // ✕ «Отмена» and Esc: revert the panel session (the page goes back to the state before the panel)
  cancelFilters() {
    this.props.onCancelModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // «Показать N результатов»: keep the changes
  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      onMapIconClick = () => {},
      onManageDisableScrolling,
      selectedFiltersCount = 0,
      noResultsInfo,
      intl,
      isMapVariant = true,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className, {
      [css.rootMapControls]: isMapVariant,
    });
    const buttonsClasses = classNames(css.buttons, {
      [css.buttonsMapVariant]: isMapVariant,
    });

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.cancel' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    return (
      <div className={classes}>
        <div className={css.searchResultSummary}>
          {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
          {listingsAreLoaded && resultsCount === 0 ? noResults : null}
          {searchInProgress ? loadingResults : null}
        </div>
        <div className={buttonsClasses}>
          <PopupOpenerButton
            id={OPENER_ID}
            className={isMapVariant ? css.filterControlMap : undefined}
            isSelected={selectedFiltersCount > 0}
            toggleOpen={this.openFilters}
          >
            <FormattedMessage
              id="SearchFiltersMobile.filtersButtonLabel"
              className={css.mapIconText}
            />
          </PopupOpenerButton>

          {sortByComponent}
          {isMapVariant ? (
            <button type="button" className={css.mapButton} onClick={onMapIconClick}>
              <span className={css.mapButtonIcon} aria-hidden="true">
                <IconLocation />
              </span>
              <span className={css.mapButtonLabel}>
                <FormattedMessage id="SearchFiltersMobile.openMapView" />
              </span>
            </button>
          ) : null}
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <Modal
          id="SearchFiltersMobile.filters"
          className={css.panelLayer}
          isClosedClassName={css.panelLayerClosed}
          isOpen={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          contentClassName={css.modalContent}
          closeButtonMessage={modalCloseButtonMessage}
          usePortal
          ariaLabelledBy={HEADING_ID}
          focusElementId={OPENER_ID}
        >
          <div className={css.modalHeadingWrapper}>
            <h2 id={HEADING_ID} className={css.modalHeading} tabIndex={-1}>
              {filtersHeading}
            </h2>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>{children}</div>
          ) : null}
          {/* The modal makes the page inert, so the panel has its own result count live region */}
          <div className={css.visuallyHidden} role="status">
            {this.state.isFiltersOpenOnMobile && listingsAreLoaded ? resultsFound : null}
          </div>

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal (starts a panel session)
 * @param {Function} props.onCloseModal - The function to close the modal and keep the changes
 * @param {Function} props.onCancelModal - The function to close the modal and revert the panel session
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const intl = useIntl();

  return <SearchFiltersMobileComponent intl={intl} {...props} />;
};

export default SearchFiltersMobile;
